import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useGoogleLogin } from "@react-oauth/google";
import logo from '../../assets/images/logo-artie.png';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const signupSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type SignupFormValues = z.infer<typeof signupSchema>;

export default function OnboardingStep1() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, checkAuth } = useAuthStore();

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  });

  const googleSignup = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      try {
        await apiClient.googleLogin(codeResponse.code);
        login();
        await checkAuth();
        toast.success("Welcome to Artie!");
        navigate("/onboarding/step2");
      } catch (error) {
        console.error("Google Signup Failed", error);
        toast.error("Google signup failed");
      }
    },
    onError: () => {
      toast.error("Google login failed");
    },
    scope: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid https://www.googleapis.com/auth/youtube.readonly"
  });

  async function onSubmit(data: SignupFormValues) {
    setIsLoading(true);
    try {
      await apiClient.signup({
        name: data.name,
        email: data.email,
        password: data.password,
      });

      await apiClient.login({
        email: data.email,
        password: data.password,
      });

      login();
      await checkAuth();
      toast.success("Account created! Welcome to Artie.");
      navigate("/onboarding/step2");
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to create account");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-hidden flex flex-col items-center justify-center font-ibm">

      {/* Progress Indicator */}
      <div className="fixed top-0 left-0 right-0 w-full max-w-5xl mx-auto px-6 py-6 flex items-center justify-between z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[oklch(0.2686_0_0)] rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm">A</div>
          <span className="text-lg font-bold tracking-tight text-[oklch(0.2686_0_0)]">Artie</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
          </div>
          <span className="text-xs font-medium text-[oklch(0.55_0_0)] ml-2">Step 1 of 3</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-full max-w-md px-6 z-10 flex flex-col items-center animate-in slide-in-from-bottom-4 duration-700 fade-in">
        {/* Artie Mascot */}
        <div className="flex flex-col mb-8 relative items-center">
          <div className="relative w-20 h-20 mb-4">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] rounded-full blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-full h-full rounded-full object-cover border-4 border-[oklch(1.0000_0_0)] shadow-xl relative z-10 animate-[float_3s_ease-in-out_infinite]"
            />
            <div className="-right-14 text-[oklch(1.0000_0_0)] transform animate-in slide-in-from-bottom-2 fade-in duration-700 delay-300 text-xs font-medium bg-orange-500 z-20 rounded-xl px-3 py-1.5 absolute top-0 shadow-lg rotate-2">
              Let's go! 🚀
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-semibold font-cabinet tracking-tight text-[oklch(0.2686_0_0)] text-center mb-2">
            Create your account
          </h1>
          <p className="text-[oklch(0.55_0_0)] font-ibm text-base text-center max-w-sm leading-relaxed">
            Join Artie to understand your reach, find brand fits, and get production-ready.
          </p>
        </div>

        {/* Google OAuth - Primary CTA */}
        <div className="w-full space-y-4 mb-6">
          <Button
            type="button"
            onClick={() => googleSignup()}
            className="w-full h-14 text-base rounded-xl bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-white flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all group"
          >
            <svg className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-[oklch(0.92_0_0)]"></div>
            <span className="flex-shrink-0 mx-4 text-[oklch(0.55_0_0)] text-xs uppercase font-medium">Or</span>
            <div className="flex-grow border-t border-[oklch(0.92_0_0)]"></div>
          </div>
        </div>

        {/* Email/Password Form */}
        <div className="w-full bg-white border border-[oklch(0.92_0_0)] rounded-2xl p-6 shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Your full name"
                        className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="you@example.com"
                        className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Min. 8 characters"
                        className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 text-base rounded-xl bg-[oklch(0.70_0.18_55)] hover:bg-[oklch(0.70_0.18_55)]/90 text-white mt-6 group"
              >
                {isLoading ? "Creating account..." : "Create Account"}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          </Form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[oklch(0.55_0_0)]">
            Already have an account?{" "}
            <a href="/login" className="font-semibold text-[oklch(0.2686_0_0)] hover:text-[oklch(0.70_0.18_55)] transition-colors">
              Log in
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}